import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext.jsx';
import api from '../utils/api.js';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheck, faEnvelope, faLock, faKey } from '@fortawesome/free-solid-svg-icons';

const ResetPassword = () => {
    const { darkMode } = useTheme();
    const [data, setData] = useState({ email: '', otp: '', password: '' });
    const [emailSent, setEmailSent] = useState('');
    const [error, setError] = useState(null);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpError, setOtpError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const otpInputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        document.title = "Reset Password | Bhishan Sah - Software Engineer";
        
        document.querySelector('meta[name="description"]')?.setAttribute(
            "content", 
            "Securely reset your password for Bhishan Sah's web development services. Quick and easy password recovery process."
        );
        
        document.querySelector('meta[name="keywords"]')?.setAttribute(
            "content", 
            "reset password, password recovery, account security, Bhishan Sah portal, forgot password"
        );

        document.querySelector('meta[property="og:title"]')?.setAttribute(
            "content", 
            "Reset Password | Bhishan Sah - Web Development Services"
        );
        
        document.querySelector('meta[property="og:description"]')?.setAttribute(
            "content", 
            "Secure password reset portal for Bhishan Sah's web development services. Reset your password with OTP verification."
        );

        document.querySelector('meta[name="twitter:title"]')?.setAttribute(
            "content", 
            "Reset Password | Bhishan Sah - Web Development Services"
        );
        
        document.querySelector('meta[name="twitter:description"]')?.setAttribute(
            "content", 
            "Secure password reset portal for Bhishan Sah's web development services. Reset your password with OTP verification."
        );

        // Check if coming from forgot password with OTP in session storage
        const resetEmail = sessionStorage.getItem('resetEmail');
        const resetOtp = sessionStorage.getItem('resetOtp');

        if (resetEmail && resetOtp) {
            // If we have OTP in session, pre-fill and verify it
            setData(prev => ({
                ...prev,
                email: resetEmail,
                otp: resetOtp
            }));
            setEmailSent(resetEmail);
            // Auto-verify the OTP
            verifyOtp(resetOtp);
        } else if (location.state?.email) {
            // Handle case where email is passed via location state
            setData(prev => ({ ...prev, email: location.state.email }));
            setEmailSent(location.state.email);
        }
    }, [location.state, verifyOtp]);

    const handleChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
        setOtpError(false);
    };

    const verifyOtp = useCallback(async (otpToVerify = null) => {
        const otp = otpToVerify || data.otp;
        if (!otp || otp.length !== 6) {
            setOtpError(true);
            toast.error('Please enter a valid 6-digit OTP');
            return false;
        }

        setLoading(true);
        try {
            const response = await api.post('/auth/verify-otp', { 
                email: data.email, 
                otp: otp 
            });
            
            if (response.status === 200) {
                setOtpVerified(true);
                setOtpError(false);
                toast.success('OTP verified successfully');
                return true;
            } else {
                throw new Error('OTP verification failed');
            }
        } catch (error) {
            setOtpVerified(false);
            setOtpError(true);
            const errorMsg = error.response?.data?.message || 'Invalid or expired OTP';
            toast.error(errorMsg);
            if (otpInputRef.current) {
                otpInputRef.current.focus();
            }
            return false;
        } finally {
            setLoading(false);
        }
    }, [data.email, data.otp]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // If OTP is not verified, try to verify it first
            if (!otpVerified) {
                const isOtpValid = await verifyOtp();
                if (!isOtpValid) {
                    setLoading(false);
                    return;
                }
            }

            // Proceed with password reset
            const response = await api.post('/auth/reset-password', { 
                email: data.email, 
                password: data.password,
                otp: data.otp
            });

            if (response.status === 200) {
                // Clear the reset email and OTP from session storage
                sessionStorage.removeItem('resetEmail');
                sessionStorage.removeItem('resetOtp');
                
                toast.success('Password reset successful! Redirecting to login...');
                setTimeout(() => {
                    navigate('/login');
                }, 1500);
            } else {
                throw new Error('Unexpected response from server');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'An error occurred. Please try again.');
            toast.error(error.response?.data?.message || 'Failed to reset password');
        }
    };

    return (
        <div className={`relative min-h-screen flex items-center justify-center px-4 lg:px-0 overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'}`}>
            {/* Background decorative blobs */}
            <div className="absolute inset-0 -z-10 pointer-events-none">
                <div className={`absolute top-14 left-8 w-72 h-72 ${darkMode ? 'bg-blue-500/10' : 'bg-blue-300/20'} rounded-full blur-3xl animate-pulse`}></div>
                <div className={`absolute bottom-14 right-8 w-96 h-96 ${darkMode ? 'bg-purple-500/10' : 'bg-purple-300/20'} rounded-full blur-3xl animate-pulse delay-1000`}></div>
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${darkMode ? 'bg-gradient-to-r from-blue-500/5 to-purple-500/5' : 'bg-gradient-to-r from-blue-200/20 to-purple-200/20'} rounded-full blur-3xl`}></div>
            </div>

            <div className={`w-full max-w-md p-8 space-y-8 rounded-2xl shadow-2xl border ${darkMode ? 'bg-gray-800/70 border-gray-700' : 'bg-white/80 backdrop-blur-sm border-gray-200'}`}>
                <div className="flex items-center justify-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${darkMode ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-gradient-to-r from-blue-500 to-purple-500'} text-white`}>🛡️</div>
                    <h1 className={`text-3xl font-extrabold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Reset Your Password</h1>
                </div>
                {emailSent && <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>OTP has been sent to: <strong>{emailSent}</strong></p>}
                {error && <p className="text-center text-red-500">{error}</p>}

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div className="relative">
                            <label htmlFor="email" className="sr-only">Email</label>
                            <FontAwesomeIcon icon={faEnvelope} className={`absolute z-30 left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                disabled
                                value={data.email}
                                onChange={handleChange}
                                className={`appearance-none rounded-t-md relative block w-full px-3 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-gray-300' : 'border-gray-300 text-gray-900'} placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm pl-10`}
                                placeholder="Email address"
                            />
                        </div>
                        <div className="relative">
                            <label htmlFor="otp" className="sr-only">OTP</label>
                            <FontAwesomeIcon icon={faKey} className={`absolute left-3 top-1/2 z-30 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <input
                                id="otp"
                                name="otp"
                                type="text"
                                required
                                ref={otpInputRef}
                                value={data.otp}
                                onChange={handleChange}
                                className={`appearance-none rounded-none relative block w-full px-3 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-gray-300' : 'border-gray-300 text-gray-900'} ${otpError ? 'border-red-500' : ''} placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm pl-10`}
                                placeholder="Enter OTP"
                            />
                            {otpVerified && <FontAwesomeIcon icon={faCheck} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" />}
                            {otpError && <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 text-sm">Invalid OTP</span>}
                        </div>
                        <div className="relative">
                            <label htmlFor="password" className="sr-only">New Password</label>
                            <FontAwesomeIcon icon={faLock} className={`absolute left-3 top-1/2 z-30 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                required
                                disabled={!otpVerified}
                                value={data.password}
                                onChange={handleChange}
                                className={`appearance-none rounded-b-md relative block w-full px-3 py-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-gray-300' : 'border-gray-300 text-gray-900'} placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm pl-10`}
                                placeholder="New Password"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            </button>
                        </div>
                    </div>

                    <div>
                        <button className='bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded'
                        >
                            {otpVerified ? 'Reset Password' : 'Verify OTP'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
